import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import {
  PAL, ZONES, STICKER_ZONE, STICKER, ORIGIN, DEFAULT_PAINT, byCode, zoneLabel, buildRenderData, renderPaint, zoneAt,
  isPaintSurcharged, paintToQuery, paintText, VIEW_W, VIEW_H,
} from '../lib/folgoPaint';
import { formatPrice } from '../lib/utils';

const ALL_ZONES = [...ZONES, STICKER_ZONE];
const pill = 'inline-flex items-center gap-1.5 rounded-full border border-[#e6e5e1] px-2.5 md:px-3.5 py-1.5 text-xs md:text-[13px] font-medium transition-colors hover:border-[#bdbcb6]';
const arrow = 'shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-full border border-[#e6e5e1] flex items-center justify-center transition-colors hover:border-[#141414]';

const loadImg = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

function usePaintRender(canvasRef, value) {
  const rdRef = useRef(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    Promise.all(['v3-src.jpg', 'v3-bg.jpg', 'v3-data.png'].map((f) => loadImg(`/folgo-paint/${f}`))).then(([a, b, d]) => {
      if (!alive) return;
      rdRef.current = buildRenderData(a, b, d);
      setReady(true);
    });
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    if (!ready) return undefined;
    const raf = requestAnimationFrame(() => renderPaint(rdRef.current, value, canvasRef.current));
    return () => cancelAnimationFrame(raf);
  }, [ready, value, canvasRef]);
  return { ready, rdRef };
}

function ZoneRow({ paint, active, setActive, onReset, lang }) {
  const idx = ALL_ZONES.findIndex((z) => z.key === active);
  const z = ALL_ZONES[idx];
  const l = zoneLabel(paint, z.key, lang);
  const go = (d) => setActive(ALL_ZONES[(idx + d + ALL_ZONES.length) % ALL_ZONES.length].key);
  return (
    <div className="flex items-center gap-2 md:gap-3" data-testid="paint-zone-row">
      <button type="button" className={arrow} onClick={() => go(-1)} aria-label="Zone précédente" data-testid="paint-zone-prev"><ChevronLeft className="w-4 h-4" /></button>
      <div className="flex-1 min-w-0 flex items-center gap-3 rounded-[10px] border border-[#141414] px-3 py-2">
        <span className="shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-lg border border-black/10" style={{ background: l.hex }} />
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-sm md:text-base leading-tight" data-testid="paint-zone-name">{lang === 'en' ? z.name_en : z.name}</span>
          <span className="block text-xs text-[#6f6f6a] truncate" data-testid="paint-picked">{l.code ? <><b className="font-mono font-medium text-[#141414]">{l.code}</b>&nbsp; </> : null}{l.name}</span>
        </span>
        <span className="hidden sm:inline font-mono text-[11px] text-[#6f6f6a]">{idx + 1}/{ALL_ZONES.length}</span>
        {z.key !== 'stickers' && paint[z.key] !== ORIGIN && (
          <button type="button" onClick={onReset} className="shrink-0 text-xs text-[#6f6f6a] underline" data-testid="paint-zone-reset">
            {lang === 'en' ? 'Reset' : <><span className="sm:hidden">Origine</span><span className="hidden sm:inline">Remettre l'origine</span></>}
          </button>
        )}
      </div>
      <button type="button" className={arrow} onClick={() => go(1)} aria-label="Zone suivante" data-testid="paint-zone-next"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );
}

function StickerPicker({ value, onChange, lang }) {
  return (
    <div className="grid grid-cols-2 gap-2.5">
      {['blanc', 'noir'].map((k) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`paint-sticker-${k}`}
          className={`rounded-xl border px-4 py-3 flex items-center justify-center gap-3 font-semibold transition-colors ${value === k ? 'border-[#141414] outline outline-1 outline-[#141414]' : 'border-[#e6e5e1] hover:border-[#bdbcb6]'}`}>
          <span className="w-8 h-8 rounded-full border border-black/15" style={{ background: STICKER[k].hex }} />
          {lang === 'en' ? STICKER[k].name_en : STICKER[k].name}
        </button>
      ))}
    </div>
  );
}

function RalPalette({ current, onPick }) {
  const [q, setQ] = useState('');
  const [hover, setHover] = useState(null);
  const search = (v) => { setQ(v); if (v.length === 4 && byCode[v]) onPick(v); };
  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between gap-2 min-h-[30px]">
        <span className="text-xs text-[#6f6f6a] truncate">{hover ? <><b className="font-mono font-medium text-[#141414]">RAL {hover.code}</b>&nbsp; {hover.name}</> : null}</span>
        <input value={q} onChange={(e) => search(e.target.value.trim())} placeholder="RAL n°" inputMode="numeric" maxLength={4} data-testid="paint-ral-search"
          className="w-[110px] rounded-full border border-[#e6e5e1] px-3 py-1.5 font-mono text-xs outline-none focus:border-[#141414]" />
      </div>
      <div className="grid grid-cols-8 sm:grid-cols-[repeat(13,minmax(0,1fr))] lg:grid-cols-[repeat(19,minmax(0,1fr))] gap-1 md:gap-[5px]">
        {PAL.map(([code, name, hex]) => (
          <button key={code} type="button" title={`RAL ${code} — ${name}`} aria-label={`RAL ${code} ${name}`} onClick={() => onPick(code)}
            onMouseEnter={() => setHover(byCode[code])} onMouseLeave={() => setHover(null)} data-testid={`paint-ral-${code}`}
            className={`h-8 md:h-10 rounded-md border border-black/10 transition-transform hover:scale-[1.08] hover:z-[1] hover:shadow-[0_2px_8px_rgba(0,0,0,.18)] ${current === code ? 'outline outline-2 outline-offset-2 outline-[#141414]' : ''}`}
            style={{ background: hex }} />
        ))}
      </div>
    </div>
  );
}

function Toolbar({ value, onChange, ready, onDownload, lang }) {
  const copy = (txt, msg) => navigator.clipboard.writeText(txt).then(() => toast.success(msg), () => toast.error(lang === 'en' ? 'Copy not possible in this browser' : 'Copie impossible dans ce navigateur'));
  return (
    <div className="flex flex-wrap justify-end gap-1.5 md:gap-2">
      <button type="button" className={pill} onClick={() => onChange(DEFAULT_PAINT)} data-testid="paint-origin-btn"><span className="hidden md:inline">{lang === 'en' ? 'Original colours' : "Couleurs d'origine"}</span><span className="md:hidden">{lang === 'en' ? 'Original' : 'Origine'}</span></button>
      <button type="button" className={pill} onClick={onDownload} disabled={!ready} data-testid="paint-download-btn"><Download className="w-3.5 h-3.5" />Image</button>
      <button type="button" className={pill} data-testid="paint-copy-link-btn"
        onClick={() => copy(`${window.location.origin}${window.location.pathname}#${paintToQuery(value)}`, lang === 'en' ? 'Link copied' : 'Lien copié')}>
        <Link2 className="w-3.5 h-3.5" />{lang === 'en' ? 'Link' : 'Lien'}
      </button>
      <button type="button" className={`${pill} bg-[#141414] text-white border-[#141414] hover:border-[#141414]`} data-testid="paint-copy-config-btn"
        onClick={() => copy(paintText(value), lang === 'en' ? 'Configuration copied' : 'Configuration copiée')}>
        <span className="hidden md:inline">{lang === 'en' ? 'Copy configuration' : 'Copier la configuration'}</span><span className="md:hidden">{lang === 'en' ? 'Copy' : 'Copier'}</span>
      </button>
    </div>
  );
}

function SurchargeBadge({ value, surcharge, surchargeFull, lang }) {
  return (
    <div className="absolute right-3 bottom-2 text-xs font-medium bg-white/90 px-2 py-1 rounded-full border border-[#e6e5e1]" data-testid="paint-surcharge-info">
      {isPaintSurcharged(value)
        ? <>{lang === 'en' ? 'Multi-colour: +' : 'Multi-teintes : +'}{surchargeFull != null && <s className="text-[#6f6f6a] mr-1" data-testid="paint-surcharge-full">{formatPrice(surchargeFull)}</s>}<span className={surchargeFull != null ? 'text-[#0047AB] font-semibold' : ''}>{formatPrice(surcharge)}</span></>
        : (lang === 'en' ? 'No surcharge' : 'Sans supplément')}
    </div>
  );
}

export default function FolgoPaintConfigurator({ value, onChange, surcharge, surchargeFull }) {
  const { language: lang } = useLanguage();
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const { ready, rdRef } = usePaintRender(canvasRef, value);
  const [active, setActive] = useState('cadre');
  const [tip, setTip] = useState(null);

  const onMove = (e) => {
    const id = zoneAt(rdRef.current, canvasRef.current, e);
    if (!id) { setTip(null); return; }
    const s = stageRef.current.getBoundingClientRect();
    const z = ALL_ZONES[id - 1];
    setTip({ x: e.clientX - s.left, y: e.clientY - s.top, label: lang === 'en' ? z.name_en : z.name });
  };
  const onClick = (e) => { const id = zoneAt(rdRef.current, canvasRef.current, e); if (id) setActive(ALL_ZONES[id - 1].key); };
  const download = () => {
    const a = document.createElement('a');
    a.download = `folgo-${ZONES.map((z) => value[z.key]).join('-')}-${value.stickers}.jpg`;
    a.href = canvasRef.current.toDataURL('image/jpeg', 0.92);
    a.click();
  };

  return (
    <div className="h-full grid grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto_auto] gap-2 md:gap-3 font-['Archivo',system-ui,sans-serif] text-[#141414]" data-testid="folgo-paint-configurator">
      <Toolbar value={value} onChange={onChange} ready={ready} onDownload={download} lang={lang} />
      <div ref={stageRef} className="relative min-h-[140px] flex items-center justify-center border-y border-[#e6e5e1] py-1">
        <canvas ref={canvasRef} width={VIEW_W} height={VIEW_H} onMouseMove={onMove} onMouseLeave={() => setTip(null)} onClick={onClick}
          className={`block max-w-full max-h-full ${tip ? 'cursor-pointer' : ''}`} data-testid="folgo-paint-canvas" aria-label="Aperçu du cadre Folgo" />
        {!ready && <div className="absolute inset-0 flex items-center justify-center bg-white text-[13px] text-[#6f6f6a]"><Loader2 className="w-5 h-5 animate-spin mr-2" />{lang === 'en' ? 'Loading…' : 'Chargement…'}</div>}
        {tip && <div className="absolute pointer-events-none bg-[#141414] text-white text-xs px-2 py-1 rounded-md whitespace-nowrap -translate-x-1/2 -translate-y-[140%]" style={{ left: tip.x, top: tip.y }}>{tip.label}</div>}
        <div className="hidden md:block absolute left-3 bottom-2 text-xs text-[#6f6f6a]">{lang === 'en' ? 'Click a part of the frame to select it' : 'Cliquez sur une zone du cadre pour la sélectionner'}</div>
        <SurchargeBadge value={value} surcharge={surcharge} surchargeFull={surchargeFull} lang={lang} />
      </div>
      <ZoneRow paint={value} active={active} setActive={setActive} onReset={() => onChange({ ...value, [active]: ORIGIN })} lang={lang} />
      <div className="relative">
        <div className={active === 'stickers' ? 'invisible' : ''} aria-hidden={active === 'stickers'}>
          <RalPalette key={active} current={value[active]} onPick={(c) => onChange({ ...value, [active]: c })} />
        </div>
        {active === 'stickers' && (
          <div className="absolute inset-0 flex items-center">
            <div className="w-full"><StickerPicker value={value.stickers} onChange={(k) => onChange({ ...value, stickers: k })} lang={lang} /></div>
          </div>
        )}
      </div>
    </div>
  );
}
